<template>
    <div class="answers display--flex">
        <div
            v-for="( answer, index ) in answers"
            class="answers__answer position--relative display--flex"
            :key="`answer-${answer.id}`"
            :style="`z-index: ${zIndexes[index]}`"
        >
            <base-field
                :class="[ ( itsChainedScript ) ? 'answers__value answers__block' : 'answers__value--sequence' ]"
                :settings="{
                    label: { class: generateLabelClass, text:  $t('global.optionWithNumber', { number: index + 1 }) },
                    input: {
                        component: 'InputBasic',
                        id: `question[${questionId}]-answer[${answer.id}]`,
                        name: `question[${questionId}]-answer[${answer.id}]`,
                        class: 'field__input--default typography--default color--default',
                        disableClass: 'field__textarea--disable',
                        value: answer.value,
                        placeholder: $t('global.addOptionWithNumber', { number: index + 1 }),
                        isMissing: isMissing
                    },
                    emitToParent: true,
                    isEdition,
                    reset,
                    allowFilling: allowFilling
                }"
                @get-value="toggleAnswers( $event, answer.id )"
            />
            <button-with-icon
                v-if="answer.value"
                :icon="{
                    component: () => import(
                        /* webpackChunkName: 'IconTrash' */ '@/components/bundles/layout/images/icons/IconTrash'
                    ),
                    class: 'answers__delete__icon'
                }"
                class="answers__delete position--absolute"
                :aria-label="$t('accessibility.delete')"
                :action="{
                    execute: true,
                    function: () => deleteAnswer( answer.id )
                }"
            />
            <base-field
                v-if="itsChainedScript"
                class="answers__block"
                :settings="{
                    label: { class: generateLabelClass, text: $t('global.nextQuestion') },
                    input: {
                        component: 'InputAutocomplete',
                        id: `next-question[${questionId}]-answer[${answer.id}]`,
                        name: `next-question[${questionId}]-answer[${answer.id}]`,
                        type: 'text',
                        value: '',
                        targetClass: 'field__input--default typography--default color--default',
                        disableClass: 'field__textarea--disable',
                        browserAutocomplete: 'off',
                        placeholder: $t('global.nextQuestion'),
                        dynamicData: {
                            data: questions,
                            apiValue: answer.nextQuestion
                        }
                    },
                    isEdition,
                    reset,
                    allowFilling: allowFilling
                }"
            />
            <base-field
                v-if="parseInt( questionType, 10 ) !== 4"
                :class="[ ( itsChainedScript ) ? 'answers__additional-info answers__block' : 'answers__additional-info--sequence' ]"
                :settings="{
                    label: { class: 'capitalize', text: $t('global.additionalAnswer') },
                    input: {
                        component: 'InputCheckbox',
                        id: `additional-behaviour-question[${questionId}]-answer[${answer.id}]`,
                        name: `additional-behaviour-question[${questionId}]-answer[${answer.id}]`,
                        type: 'checkbox',
                        class: 'field__input--checkbox',
                        value: answer.additionalBehaviour,
                        browserAutocomplete: 'off',
                        isRequired: false
                    },
                    isEdition,
                    reset,
                    allowFilling: true
                }"
            />
        </div>
    </div>
</template>

<script>
// packages
import { ref, onUnmounted, watch } from '@vue/composition-api';

// store
import { useActions } from '@/hooks/use-store';

// hooks
import generateZindex from '@/hooks/use-z-index';

export default {
    components: {
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' ),
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' )
    },
    props: {
        questions: Array,
        questionId: Number,
        questionType: [ Number, String ],
        isMissing: Boolean,
        isEdition: Boolean,
        itsChainedScript: Boolean,
        reset: Boolean,
        apiAnswers: Array,
        generateLabelClass: String,
        allowFilling: Boolean
    },
    setup ( props ) {
        // definção da estrutura inicial das respostas.
        const defaultAnswers = props.apiAnswers.length
            ? [ ...props.apiAnswers, { id: props.apiAnswers[props.apiAnswers.length - 1].id + 1, value: '' } ]
            : [ { id: 1, value: '' } ];
        const answers = ref( defaultAnswers );

        // obtenção das acções
        const { actions } = useActions();

        /**
         * [lifecycle]
         * Na destruição do componente é necessário
         * limpar a store com as respostas que foram preenchidas
         * pelo utilizador.
         *
         * @since 1.0.0
         * @category lifecycle
         * @autor Tiago Martinho
         *
        */
        onUnmounted( () => {
            answers.value.forEach( ({ id }) => {
                actions( 'deleteField', { name: `question[${props.questionId}]-answer[${id}]` });
            });
        });

        /**
         * [method]
         * Actualização do valor da resposta.
         *
         * @param {Number} value
         * @param {Number} answerId
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const updateAnswerValue = ( value, answerId ) => {
            answers.value.forEach( answer => {
                if ( answer.id === answerId )
                    answer.value = value;
            });
        };

        /**
         * [method]
         * Adicionado a nova possibilidade de reposta
         * após a inserção do valor da anterior.
         *
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const addNewAnswer = () => {
            const generateAnswerId = answers.value[answers.value.length - 1].id + 1;
            answers.value.push({ id: generateAnswerId, value: '' });
        };

        /**
         * [method]
         * Procedimento de actualização do valor da resposta
         * preenchida e adição da nova possibilidade de reposta.
         *
         * @param {Number} value
         * @param {Number} answerId
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const toggleAnswers = ( value, answerId ) => {
            if ( props.isEdition && value === '' )
                return;

            // obtenção do valor da resposta.
            const haveValue = answers.value.find( ({ id }) => id === answerId ).value;

            // validação se o valor da resposta já existe.
            // caso não exista, quer dizer que é para adicionar uma
            // nova possibilidade.
            if ( !haveValue )
                addNewAnswer();

            // só é necessário quando se encontra
            // no modo de edição.
            updateAnswerValue( value, answerId );
        };

        /**
         * [method]
         * Removido a possibilidade de resposta
         * do array de respostas.
         *
         * @param {Number} answerId
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const deleteAnswer = answerId => {
            // Tenta obter a resposta através do id da resposta
            const apiAnswer = props.apiAnswers.find( answer => answer.id === answerId );

            // Se for um id de resposta já existente, manda uma key para o payload
            // para saber que é para se apagar essa resposta na base de dados.
            if ( apiAnswer ) {
                actions( 'registerField', { name: `delete-question[${props.questionId}]-answer[${answerId}]`, value: answerId });
                apiAnswer.operation = 'delete';
            }

            // eslint-disable-next-line
            answers.value = answers.value.filter( answer => {
                if ( answer.id !== answerId )
                    return answer;

                actions( 'deleteField', { name: `question[${props.questionId}]-answer[${answerId}]` });
            });
        };

        // obtenção dos z-indexes
        const zIndexes = generateZindex( answers );

        // Aquando o cancelamento da edição por parte do utilizador
        // é necessário gerar as respostas iniciais do script.
        watch( () => props.apiAnswers,
            newApiAnswers => {
                if ( props.reset ) {
                    answers.value = [];
                    answers.value = [ ...newApiAnswers, { id: newApiAnswers[newApiAnswers.length - 1].id + 1, value: '' } ];

                    answers.value.forEach( answer => {
                        const input = document.getElementById( `question[${props.questionId}]-answer[${answer.id}]` );
                        input.value = answer.value;
                        // trigger do evento `input`.
                        const event = new Event( 'input' );
                        input.dispatchEvent( event );
                    });
                }
            }, {
                deep: true
            });

        return {
            answers,
            toggleAnswers,
            deleteAnswer,
            zIndexes
        };
    }
};
</script>

<style lang="scss" scoped>
    .answers {
        grid-column: 1 / -1;
        flex-direction: column;
        row-gap: 10px;
        &__answer {
            gap: 30px;
        }
        &__block {
            width: 30%;
        }
        &__additional-info {
            text-align: center;
            &--sequence {
                width: 175px;
            }
        }
        &__additional-info.answers__block {
            width: 24%;
        }
        &__value {
            max-width: 285px;
            &--sequence {
                width: calc( 100% - 260px );
            }
        }
        /deep/ &__delete {
            @include transform-position( false, -5px, -13px, false );
            &__icon{
                @include dimensions( auto, 52px );
            }
        }
    }
</style>